import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import State from "../../../../../../context";
import { Flex } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ModalCompanyUserArchive from "../PageCompanies/component/ModalCompanyUserArchive";
import ModalCompanyUserSettings from "../../component/ModalCompanyUserSettings";
import ModalViewFolders from "./component/SectionUserPermission/component/ModalViewFolders";
import SectionUserAll from "./component/SectionUserAll";
import SectionUserPermission from "./component/SectionUserPermission";

import StyledContainer from "./style";

import {
  archiveCompanyUser,
  generateJwtBySuperadmin,
  getCompanyAll,
  getCompanyMarketingMaterials,
  getCompanyUsersByJwt,
  getUserTypes,
  updateCompanyUserBySuperadmin,
} from "../../request";

import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import {
  isPermissionReadonly,
  isTypeAffiliateAdvisor,
  isTypeLevelWealthAdvisor,
  isTypeLevelWealthEnterpriseAdmin,
  isTypeLevelWealthEnterpriseAdminAdvisor,
  isTypeProPortalAdvisor,
  isTypeProPortalAffiliateAdvisor,
  isTypeSuperAdmin,
} from "../../helper";
import Cookies from "js-cookie";

const PageUsers = () => {
  const [state] = useContext(State);
  const [searchParams] = useSearchParams();
  const [companyMarketingMaterial, setCompanyMarketingMaterials] = useState();

  const activeClientData = state.listUsers?.find(
    it => it.id === state.activeUserId
  );

  useEffect(() => {
    state.setKeyValue("activeSection", "userAll");

    state.setKeyValue("loading", true);

    if (!state.userTypes) {
      getUserTypes()
        .then(data => state.setKeyValue("userTypes", data))
        .catch(state.showError);
    }

    if (!state.listCompanies) {
      getCompanyAll({ userEmail: searchParams.get("email") })
        .then(data => state.setKeyValue("listCompanies", data))
        .catch(state.showError);
    }

    getCompanyUsersByJwt()
      .then(data => state.setKeyValue("listUsers", data))
      .catch(state.showError)
      .finally(() => state.setKeyValue("loading", false));
  }, []);

  const handleCompanyUserArchive = () => {
    state.setKeyValue("loading", true);

    archiveCompanyUser({
      companyId: state.activeCompanyId,
      companyUserId: state.activeUserId,
      userEmail: state._id,
    })
      .then(() =>
        getCompanyUsersByJwt()
          .then(data => {
            state.setKeyValue("listUsers", data);
            state.showSuccess("User archived!");
            state.setKeyValue("openModalCompanyUserArchive", false);
          })
          .catch(state.showError)
          .finally(() => state.setKeyValue("loading", false))
      )
      .catch(error => {
        state.showError(error);
        state.setKeyValue("loading", false);
      });
  };

  const handleLaunch = () => {
    generateJwtBySuperadmin({
      clientEmail: activeClientData.email,
      userEmail: state._id,
    })
      .then(response => {
        Cookies.set(
          `jwt__${activeClientData.email.toLowerCase()}`,
          response.jwt,
          {
            expires: 0.5, // half a day (12 hours)
            secure: true,
          }
        );

        const userType = response?.subscription?.userType;

        if (isTypeSuperAdmin(userType)) {
          window.open(
            `/super-admin/companies?email=${encodeURIComponent(activeClientData.email)}`,
            isPermissionReadonly(state.permissions?.accessLevel)
              ? "windowReadonly"
              : ""
          );
        } else if (
          isTypeLevelWealthEnterpriseAdmin(userType) ||
          isTypeLevelWealthEnterpriseAdminAdvisor(userType)
        ) {
          window.open(
            `/wealth-admin-portal/dashboard?email=${encodeURIComponent(activeClientData.email)}`,
            isPermissionReadonly(state.permissions?.accessLevel)
              ? "windowReadonly"
              : ""
          );
        } else if (
          isTypeLevelWealthAdvisor(userType) ||
          isTypeProPortalAffiliateAdvisor(userType) ||
          isTypeProPortalAdvisor(userType) ||
          isTypeAffiliateAdvisor(userType)
        ) {
          window.open(
            `/wealth-advisor-portal/leads?email=${encodeURIComponent(activeClientData.email)}`,
            isPermissionReadonly(state.permissions?.accessLevel)
              ? "windowReadonly"
              : ""
          );
        }
      })
      .catch(state.showError);
  };

  const handleViewFolders = () => {
    state.setKeyValue("loading", true);

    getCompanyMarketingMaterials({
      companyId: activeClientData.companyId,
      userEmail: state._id,
    })
      .then(data => {
        setCompanyMarketingMaterials(data);
        state.setKeyValue("openModalViewFolders", true);
      })
      .catch(state.showError)
      .finally(() => state.setKeyValue("loading", false));
  };

  const submitCompanyUserSettings = values => {
    state.setKeyValue("loading", true);

    const requestPayload = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phone: values.phone + "",
      phoneAlt: (values.phoneAlt ?? "") + "",
      type: values.type,
    };

    updateCompanyUserBySuperadmin({
      companyId: state.activeCompanyId,
      companyUserId: state.activeUserId,
      requestPayload,
      userEmail: state._id,
    })
      .then(() =>
        getCompanyUsersByJwt()
          .then(data => {
            state.setKeyValue("listUsers", data);
            state.showSuccess("New user added.");
            state.setKeyValue("openModalCompanyUserSettings", false);
          })
          .catch(state.showError)
          .finally(() => state.setKeyValue("loading", false))
      )
      .catch(error => {
        state.showError(error);
        state.setKeyValue("loading", false);
      });
  };

  const submitPermissions = updatedPermissions => {
    state.setKeyValue("loading", true);

    const requestPayload = {
      email: activeClientData.email,
      firstName: activeClientData.firstName,
      lastName: activeClientData.lastName,
      permissions: {
        ...activeClientData.permissions,
        ...updatedPermissions,
      },
      phone: activeClientData.phone ?? "",
      phoneAlt: activeClientData.phoneAlt ?? "",
      type: activeClientData.type,
    };

    updateCompanyUserBySuperadmin({
      companyId: activeClientData.companyId,
      companyUserId: activeClientData.id,
      requestPayload,
      userEmail: state._id,
    })
      .then(() =>
        getCompanyUsersByJwt()
          .then(data => {
            state.setKeyValue("listUsers", data);
            state.showSuccess("User updated!");
          })
          .catch(state.showError)
          .finally(() => state.setKeyValue("loading", false))
      )
      .catch(error => {
        state.showError(error);
        state.setKeyValue("loading", false);
      });
  };

  const handleEditActiveClient = () => {
    state.setKeyValue("activeCompanyId", activeClientData.companyId);
    state.setKeyValue("activeUserId", activeClientData.id);
    state.setKeyValue("openModalCompanyUserSettings", true);
  };

  return (
    <StyledContainer>
      {state.activeSection === "userOne" ? (
        <SectionUserPermission
          allowEdit={!isPermissionReadonly(state.permissions?.accessLevel)}
          handleEditActiveClient={handleEditActiveClient}
          handleLaunch={handleLaunch}
          handleViewFolders={handleViewFolders}
          itemsBreadcrumb={[
            {
              onClick: () => state.setKeyValue("activeSection", "userAll"),
              title: (
                <Flex align="center" gap={8} style={{ cursor: "pointer" }}>
                  <FontAwesomeIcon
                    icon={faArrowLeft}
                    style={{ fontSize: 14 }}
                  />
                  <b>All Users</b>
                </Flex>
              ),
            },
            {
              title: <span>User Profile</span>,
            },
          ]}
          loading={state.loading}
          submitPermissions={submitPermissions}
          userData={activeClientData}
          userTypes={state.userTypes}
        />
      ) : (
        <SectionUserAll />
      )}

      <ModalCompanyUserArchive
        companyUserData={state.listUsers?.find(
          it => it.id === state.activeUserId
        )}
        loading={state.loading}
        onCancel={() => state.setKeyValue("openModalCompanyUserArchive", false)}
        onConfirm={handleCompanyUserArchive}
        open={state.openModalCompanyUserArchive}
      />
      <ModalCompanyUserSettings
        companyName={
          state.listCompanies?.find(it => it.id === state.activeCompanyId)?.name
        }
        companyType={
          state.listCompanies?.find(it => it.id === state.activeCompanyId)?.type
        }
        loading={state.loading}
        onCancel={() =>
          state.setKeyValue("openModalCompanyUserSettings", false)
        }
        onFinish={submitCompanyUserSettings}
        open={state.openModalCompanyUserSettings}
        userData={state.listUsers?.find(it => it.id === state.activeUserId)}
        userTypes={state.userTypes}
      />
      <ModalViewFolders
        marketingMaterialsData={companyMarketingMaterial}
        onCancel={() => state.setKeyValue("openModalViewFolders", false)}
        open={state.openModalViewFolders}
      />
    </StyledContainer>
  );
};

export default PageUsers;
